/**
 * Die Abrufe hinter realtimeProjectSync — getrennt, damit die Regeln dort ohne
 * Supabase-Client pruefbar bleiben.
 *
 * ─── Warum die Form der PROJEKTLISTE und nicht die der Einzeluebersicht ────
 * Geprueft am 21.10.2026, alle Verbraucher von `projects` in App.tsx und
 * darunter (Dashboard, ProjectOverview, AdminView/Alerts/Substitution,
 * GlobalMap, TasksView, HolidayPlanner, mobile Ansichten, Wiederherstellen
 * nach F5): keiner liest Maengel (`data.items`, `inspection_items`) oder
 * Blob-Zweige ausser `metadata` von einem Listeneintrag. Das geoeffnete
 * Projekt kommt immer aus `loadProjectById` (handleOpenProject) und lebt in
 * `report`. Umgekehrt lesen AdminAlertsView, AdminView (Zaehler),
 * AdminSubstitutionView und dashboardModel `metadata.globalData.services` —
 * das baut nur die Listenform aus `project_services`; die bisherige
 * Uebersichtsform liess dort den leeren Blob-Zweig stehen (saveProjectToCloud
 * leert ihn), und die Warnungen fehlten fuer jedes per Realtime
 * aktualisierte Projekt.
 *
 * ─── Was geladen wird ──────────────────────────────────────────────────────
 *  - Payload mit Blob: nur `project_services`, eingebettet unter `projects`
 *    (die Einbettung kennt die 1.000-Zeilen-Grenze von PostgREST nicht, eine
 *    flache Abfrage ueber 50 Projekte haette sie).
 *  - Payload ohne Blob: die Zeile der Projektliste, dieselbe Abfrage wie der
 *    Listen-Load, nur fuer diese Kennungen.
 * Beide mit dem Spalten-Rueckfall (spaltenRueckfall.ts) und in Buendeln zu 50.
 *
 * ─── Der Abgleich (Nr. 175, projektListeDelta.ts) ─────────────────────────
 * Dazu die Abrufe des Abgleichs: die Signale ab einem Stand
 * (`projekt_signale`), die Kennungen aller sichtbaren Projekte (dieselbe
 * Keyset-Abfrage wie der Listen-Load) und die Listenzeilen einzelner
 * Projekte — dieselbe Abfrage und dieselben Angleichungen wie der Listen-Load.
 */

import { supabase } from './supabaseClient';
import { PROJECT_SERVICES_COLUMNS, baseColumn, projektListeSelect, spaltenSchluessel } from './queryColumns';
import { alsAbfrageFehler, fehlendeSpalten, mitSpaltenRueckfall } from './spaltenRueckfall';
import { setLinkedParentName } from './projectLink';
import { fetchParentNames } from './projectLinkService';
import { bildeListenEintrag, zeileAusPayload, type Vormerkung, type Zeile } from './realtimeProjectSync';
import { alsListProject, type ListProject } from './listProject';
import { SIGNAL_SEITE, leseSignalZeile, type Signal } from './projektListeDelta';

const BUENDEL = 50;

const inBuendeln = (ids: string[]): string[][] => {
    const buendel: string[][] = [];
    for (let i = 0; i < ids.length; i += BUENDEL) buendel.push(ids.slice(i, i + BUENDEL));
    return buendel;
};

/** `id, project_services(...)` — ohne die als fehlend bekannten Spalten. */
const leistungenSelect = (): string => {
    const fehlend = fehlendeSpalten();
    const spalten = PROJECT_SERVICES_COLUMNS
        .filter((s) => !fehlend.has(spaltenSchluessel('public.project_services', baseColumn(s))));
    return `id, project_services(${spalten.join(', ')})`;
};

const ladeZeilen = async (herkunft: string, select: () => string, ids: string[]): Promise<Zeile[]> => {
    const ergebnis: Zeile[] = [];
    for (const kennungen of inBuendeln(ids)) {
        const zeilen = await mitSpaltenRueckfall(herkunft, async () => {
            const { data, error } = await supabase.from('projects').select(select()).in('id', kennungen);
            if (error) throw alsAbfrageFehler(error);
            return (data || []) as unknown as Zeile[];
        });
        ergebnis.push(...zeilen);
    }
    return ergebnis;
};

export interface LadeOptionen {
    /** Name eines Projekts aus der aktuellen Liste (fuer die Mutter einer Tochter). */
    nameAusListe: (id: string) => string | undefined;
}

/**
 * Laedt die vorgemerkten Projekte in Listenform. Projekte, die die Datenbank
 * nicht (mehr) liefert — geloescht, Rechte entzogen —, fehlen im Ergebnis;
 * ein Fehler des Abrufs wirft.
 */
export const ladeRealtimeEintraege = async (
    vormerkungen: Map<string, Vormerkung>,
    opts: LadeOptionen,
): Promise<ListProject[]> => {
    const ausPayload = new Map<string, Zeile>();
    const zuLaden: string[] = [];
    for (const [id, v] of vormerkungen) {
        const zeile = zeileAusPayload(v.neu, v.fehler);
        if (zeile) ausPayload.set(id, zeile);
        else zuLaden.push(id);
    }

    const roh: Zeile[] = [];
    if (ausPayload.size > 0) {
        const leistungen = await ladeZeilen(
            'realtimeProjektLader (Positionen zum Realtime-Payload)',
            leistungenSelect,
            [...ausPayload.keys()],
        );
        for (const l of leistungen) {
            const zeile = typeof l.id === 'string' ? ausPayload.get(l.id) : undefined;
            if (zeile) roh.push({ ...zeile, project_services: l.project_services ?? [] });
        }
    }
    if (zuLaden.length > 0) {
        roh.push(...await ladeZeilen(
            'realtimeProjektLader (Listenzeile)',
            () => projektListeSelect(fehlendeSpalten()),
            zuLaden,
        ));
    }

    return baueEintraege(roh, opts);
};

/** Die Angleichungen der Projektliste und die Namen der Muetter (ein Abruf fuer alle fehlenden). */
const baueEintraege = async (roh: Zeile[], opts: LadeOptionen): Promise<ListProject[]> => {
    const gebaut = roh.map((z) => bildeListenEintrag(z));
    // Muetter: erst in der Liste nachsehen, nur was dort fehlt, in einem Abruf.
    const offen = gebaut.filter((g) => g.mutterId && !opts.nameAusListe(g.mutterId)).map((g) => g.mutterId as string);
    const namen = offen.length > 0 ? await fetchParentNames(offen) : new Map<string, string>();
    for (const g of gebaut) {
        if (g.mutterId) setLinkedParentName(g.eintrag, opts.nameAusListe(g.mutterId) ?? namen.get(g.mutterId));
    }
    return gebaut.map((g) => alsListProject(g.eintrag));
};

// ═══════════════════════════════════════════════════════════════════════════
// DER ABGLEICH — Signale, sichtbare Kennungen, Listenzeilen
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Laedt Projekte in Listenform — dieselbe Abfrage und dieselben Angleichungen
 * wie der Listen-Load, in Buendeln zu 50. Was die Datenbank nicht liefert
 * (geloescht, entzogen), fehlt im Ergebnis; ein Fehler wirft.
 */
export const ladeListenEintraege = async (ids: string[], opts: LadeOptionen): Promise<ListProject[]> => {
    if (ids.length === 0) return [];
    const roh = await ladeZeilen(
        'realtimeProjektLader (Abgleich, Listenzeile)',
        () => projektListeSelect(fehlendeSpalten()),
        ids,
    );
    return baueEintraege(roh, opts);
};

/** Fehlt `projekt_signale` (Nr. 175 nicht gelaufen)? 42P01 kommt von Postgres, PGRST205 vom Schema-Cache. */
export const istFehlendeSignalTabelle = (e: unknown): boolean => {
    const code = (e as { code?: unknown })?.code;
    return code === 'PGRST205' || code === '42P01';
};

export interface SignalAbruf {
    /** Aelteste zuerst. */
    signale: Signal[];
    /** Die Seite ist voll — es gibt (vermutlich) mehr: Voll-Load. */
    voll: boolean;
}

/**
 * Die Signale ab `seit` (einschliesslich; `null` = alle), aelteste zuerst,
 * hoechstens eine Seite. Wirft bei jedem Fehler — auch, wenn die Tabelle
 * fehlt (`istFehlendeSignalTabelle`).
 */
export const leseSignaleSeit = async (seit: string | null): Promise<SignalAbruf> => {
    let abfrage = supabase
        .from('projekt_signale')
        .select('id, project_id, geaendert_am, geloescht')
        .order('geaendert_am', { ascending: true })
        .order('id', { ascending: true })
        .limit(SIGNAL_SEITE);
    if (seit) abfrage = abfrage.gte('geaendert_am', seit);
    const { data, error } = await abfrage;
    if (error) throw alsAbfrageFehler(error);
    const zeilen = (data || []) as unknown[];
    const signale: Signal[] = [];
    for (const z of zeilen) {
        const s = leseSignalZeile(z);
        if (s) signale.push(s);
    }
    return { signale, voll: zeilen.length >= SIGNAL_SEITE };
};

/** Wie viele der juengsten Signale vor einem Voll-Load gelesen werden. */
export const VORLAUF_SIGNALE = 100;

/**
 * Vor einem Voll-Load: die juengsten Signale. Das juengste ist der
 * Wasserstand der Liste NACH dem Load; alle werden VOR dem Load gesichtet
 * (projektListeDelta, Sichtungsbuch) — sie waren festgeschrieben, bevor
 * geladen wurde, der Load deckt sie ab, und der erste Abgleich danach
 * ueberspringt sie. Wirft wie `leseSignaleSeit`.
 */
export const leseSignalVorlauf = async (anzahl: number = VORLAUF_SIGNALE): Promise<{ signale: Signal[]; wasserstand: string | null }> => {
    const { data, error } = await supabase
        .from('projekt_signale')
        .select('id, project_id, geaendert_am, geloescht')
        .order('geaendert_am', { ascending: false })
        .order('id', { ascending: false })
        .limit(anzahl);
    if (error) throw alsAbfrageFehler(error);
    const signale: Signal[] = [];
    for (const z of (data || []) as unknown[]) {
        const s = leseSignalZeile(z);
        if (s) signale.push(s);
    }
    return { signale, wasserstand: signale[0]?.stand ?? null };
};

/**
 * Die Kennungen aller Projekte, die der Server dem Leser JETZT zeigt — per
 * Keyset wie der Listen-Load (cloudService.doLoadProjectsFromCloud): ein
 * Fenster nach Offset koennte bei einer Loeschung zwischen zwei Seiten eine
 * Kennung verlieren. Damit faellt auf, was ohne Signal verschwand oder
 * hinzukam (Rechte, Vertretung). Wirft bei jedem Fehler.
 */
export const leseSichtbareKennungen = async (): Promise<Set<string>> => {
    const SEITE = 1000;
    const ids = new Set<string>();
    for (let letzte: string | null = null; ; ) {
        let abfrage = supabase.from('projects').select('id').order('id', { ascending: true }).limit(SEITE);
        if (letzte !== null) abfrage = abfrage.gt('id', letzte);
        const { data, error } = await abfrage;
        if (error) throw alsAbfrageFehler(error);
        const seite = ((data || []) as Array<{ id?: unknown }>)
            .map((z) => z.id)
            .filter((id): id is string => typeof id === 'string' && id !== '');
        for (const id of seite) ids.add(id);
        if (seite.length < SEITE) break;
        letzte = seite[seite.length - 1];
    }
    return ids;
};
